import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Table, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useStockTransfers } from '../../contextjs/StockTransferContext';
import { useUser } from '../../UserContext';
import { fetchAllProductsMinimalWithUnits, fetchCurrentStore, fetchStores } from '../../api/api';

const initialItem = { product: "", quantity: "", unit: "", conversion_factor: 1, availableUnits: [] };

export default function StockTransferPage() {
    const { transfers, requests, alerts, loading, loadTransfers, loadRequests, loadAlerts, addTransfer, addRequest,
        approveRequest, rejectRequestAction} = useStockTransfers();
    const { user } = useUser();

    const navigate = useNavigate();

    const [stores, setStores] = useState([]);
    const [currentStore, setCurrentStore] = useState(null);
    const [allProducts, setAllProducts] = useState([]);

    const [activeTab, setActiveTab] = useState('alerts');
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewModalType, setViewModalType] = useState(null); // 'request' | 'transfer'
    const [viewRecord, setViewRecord] = useState(null);
    const [items, setItems] = useState([{...initialItem}]);
    const [requestForm, setRequestForm] = useState({
        requesting_store: "", source_store: "", remarks: "",
    });
    const [transferForm, setTransferForm] = useState({
        from_store: "", to_store: "", remarks: "",
        });
    const [message, setMessage] = useState(null);
    const isAdmin = user?.is_superuser || user?.is_staff;
    const isWarehouse = currentStore?.code === 'WH';

    const handleBack = () => {
        navigate(-1);
    };

    const warehouseStore = useMemo(
        () => (Array.isArray(stores) ? stores : []).find((s) => s?.code === 'WH') || null,
        [stores]
    );

    const storeNameById = useMemo(() => {
        const map = new Map();
        (Array.isArray(stores) ? stores : []).forEach((s) => {
            if (s?.id != null) map.set(String(s.id), s.name);
        });
        return map;
    }, [stores]);

    const productById = useMemo(() => {
        const map = new Map();
        (Array.isArray(allProducts) ? allProducts : []).forEach((p) => {
            if (p?.id != null) map.set(String(p.id), p);
        });
        return map;
    }, [allProducts]);

    const getStoreName = (storeId) => {
        if (storeId == null || storeId === '') return '-';
        return storeNameById.get(String(storeId)) || String(storeId);
    };

    const getProductName = (productId) => {
        if (productId == null || productId === '') return '-';
        const p = productById.get(String(productId));
        return p?.product_name || String(productId);
    };

    const getUnitLabel = (productId, unitId) => {
        if (unitId == null || unitId === '') return '-';

        const p = productById.get(String(productId));
        const base = p?.base_unit;
        if (base?.id != null && String(base.id) === String(unitId)) {
            return base.abbreviation || base.name || String(unitId);
        }

        const alt = (Array.isArray(p?.units) ? p.units : []).find(
            (u) => String(u?.unit?.id) === String(unitId)
        );
        if (alt?.unit) {
            return alt.unit.abbreviation || alt.unit.name || String(unitId);
        }

        return String(unitId);
    };

    const openViewModal = (type, record) => {
        setViewModalType(type);
        setViewRecord(record || null);
        setShowViewModal(true);
    };

    useEffect(() => {
        loadTransfers();
        loadRequests();
        loadAlerts();
    }, [loadTransfers, loadRequests, loadAlerts]);

    useEffect(() => {
        let cancelled = false;

        const loadStoreContext = async () => {
            try {
                const [storesData, currentStoreData] = await Promise.all([
                    fetchStores(),
                    fetchCurrentStore(),
                ]);
                if (cancelled) return;

                setStores(Array.isArray(storesData) ? storesData : (storesData?.results || []));
                setCurrentStore(currentStoreData || null);
            } catch (error) {
                console.error('Error loading stores/current store:', error);
                if (cancelled) return;
                setStores([]);
                setCurrentStore(null);
            }
        };

        loadStoreContext();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const load = async () => {
            const data = await fetchAllProductsMinimalWithUnits();
            setAllProducts(Array.isArray(data) ? data : (data?.results || []));
        };
        load();
    }, []);

    useEffect(() => {
        if (currentStore?.id) {
            setRequestForm((prev) => ({...prev, requesting_store: currentStore.id }));
            setTransferForm((prev) => ({...prev, from_store: currentStore.id }));
        }
    }, [currentStore]);

    useEffect(() => {
        // For non-warehouse stores, requests must originate from the current store and be sent to the warehouse.
        if (!isWarehouse && currentStore?.id && warehouseStore?.id) {
            setRequestForm((prev) => ({
                ...prev,
                requesting_store: currentStore.id,
                source_store: warehouseStore.id,
            }));
        }
    }, [isWarehouse, currentStore, warehouseStore]);

    const pendingRequests = useMemo(
        () => requests.filter((request) => request.status === 'PENDING'),
        [requests]
    );

    const handleApprove = async (requestId) => {
        setMessage(null);
        try {
            await approveRequest(requestId);
            setMessage({ type: 'success', text: `Request #${requestId} approved successfully.` });
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to approve request.',
            });
        }
    };

    const handleReject = async (requestId) => {
        setMessage(null);
        try {
            await rejectRequestAction(requestId);
            setMessage({ type: 'success', text: `Request #${requestId} rejected.` });
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to reject request.',
            });
        }
    };

    const handleItemChange = (index, field, value) => {
        const updated = [...items];
        updated[index][field] = value;
        setItems(updated);
    };

    const buildAvailableUnits = (product) => {
        const availableUnits = [];

        // base unit first (conversion = 1)
        if (product?.base_unit?.id) {
            availableUnits.push({
                id: product.base_unit.id,
                name: product.base_unit.name,
                abbreviation: product.base_unit.abbreviation,
                conversion_factor: 1,
                is_base: true,
            });
        }

        // alternative units
        if (Array.isArray(product?.units) && product.units.length) {
            product.units.forEach((u) => {
                if (!u?.unit?.id) return;
                availableUnits.push({
                    id: u.unit.id,
                    name: u.unit.name,
                    abbreviation: u.unit.abbreviation,
                    conversion_factor: u.conversion_factor,
                    is_base: false,
                });
            });
        }

        return availableUnits;
    };

    const handleProductSelect = (index, productId) => {
        const updated = [...items];

        updated[index].product = productId;

        const product = (Array.isArray(allProducts) ? allProducts : []).find(
            (p) => String(p.id) === String(productId)
        );

        const availableUnits = buildAvailableUnits(product);
        updated[index].availableUnits = availableUnits;

        // default to base unit (or the only available unit)
        const defaultUnit = availableUnits[0] || null;
        updated[index].unit = defaultUnit ? defaultUnit.id : '';
        updated[index].conversion_factor = defaultUnit ? (defaultUnit.conversion_factor || 1) : 1;

        setItems(updated);
    };

    const handleUnitSelect = (index, unitId) => {
        const updated = [...items];
        updated[index].unit = unitId;

        const selectedUnit = (updated[index].availableUnits || []).find(
            (u) => String(u.id) === String(unitId)
        );
        updated[index].conversion_factor = selectedUnit ? (selectedUnit.conversion_factor || 1) : 1;
        setItems(updated);
    };

    const addLine = () => setItems([...items, {...initialItem}]);
    const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

    const resetItems = () => setItems([{...initialItem}]);

    const submitRequest = async (e) => {
        e.preventDefault();

        if (!isWarehouse) {
            if (!requestForm?.requesting_store || !requestForm?.source_store) {
                setMessage({
                    type: 'danger',
                    text: warehouseStore
                        ? 'Please wait for store context to load and try again.'
                        : 'Warehouse store could not be determined. Please contact an administrator.',
                });
                return;
            }
        }
        const payloadItems = items.map((it) => ({
            product: it.product,
            quantity: it.quantity,
            unit: it.unit,
            conversion_factor: it.conversion_factor,
        }));

        if (payloadItems.some((it) => !it.product || !it.quantity || !it.unit)) {
            setMessage({ type: 'danger', text: 'Please select product, quantity, and unit for all items.' });
            return;
        }

        try {
            await addRequest({ ...requestForm, items: payloadItems });
            setMessage({ type: 'success', text: 'Transfer Request submitted successfully!' });
            setShowRequestModal(false);
            resetItems();
        } catch (error) {
            setMessage({ type: 'danger',
                text: error?.response?.data?.detail || 'Failed to create transfer request.'
            });
        }
    };

    const submitTransfer = async (e) => {
        e.preventDefault();
        const payloadItems = items.map((it) => ({
            product: it.product,
            quantity: it.quantity,
            unit: it.unit,
            conversion_factor: it.conversion_factor,
        }));

        if (payloadItems.some((it) => !it.product || !it.quantity || !it.unit)) {
            setMessage({ type: 'danger', text: 'Please select product, quantity, and unit for all items.' });
            return;
        }
        try {
            await addTransfer({ ...transferForm, items: payloadItems });
            setMessage({ type: 'success', text: 'Transfer completed successfully!' });
            setShowTransferModal(false);
            resetItems();
        } catch (error) {
            setMessage({ type: 'danger',
                text: error?.response?.data?.detail || 'Failed to complete transfer.'
            });
        }
    };

    const productOptions = useMemo(
        () => (Array.isArray(allProducts) ? allProducts : []).map((p) => ({
            value: p.id,
            label: p.product_name,
        })),
        [allProducts]
    );

    return (
        <div className="container-fluid py-4">
            <div className="back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <Row className="mb-4">
                <Col md={8}>
                    <h3 className="fw-bold mb-1">Stock Transfer Control Center</h3>
                    <p className="text-muted mb-0">
                        Move products between stores, surface low stock levels, and approval flow
                    </p>
                </Col>
                <Col md={4} className="text-md-end mt-3 mt-md-0">
                    {!isWarehouse ? (
                        <Button
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => {
                                resetItems();
                                setShowRequestModal(true);
                            }}
                        >
                            Request Transfer
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={() => {
                                resetItems();
                                setShowTransferModal(true);
                            }}
                        >
                            New Direct Transfer
                        </Button>
                    )}
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-muted small">Low Stock Alerts</div>
                                    <h2 className="fw-bold mb-0">{alerts.length}</h2>
                                </div>
                                <Badge bg={alerts.some(a => a.severity === 'critical') ? 'danger' : 'warning'}>
                                    Live
                                </Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body>
                            <div className="text-muted small">Pending Request</div>
                            <h2 className="fw-bold mb-0">{pendingRequests.length}</h2>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                        <Nav.Item>
                            <Nav.Link eventKey="alerts">Low Stock Alerts</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="requests">Transfer Requests</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="transfers">Transfers</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <div className="pt-4">
                        {loading && <Spinner animation="border" />}

                        {activeTab === 'alerts' && (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Current Stock</th>
                                        <th>Reorder Level</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alerts.length ? alerts.map((alert) => (
                                        <tr key={alert.product_id}>
                                            <td>{alert.product_name}</td>
                                            <td>{alert.stock}</td>
                                            <td>{alert.reorder_level}</td>
                                            <td>
                                                <Badge bg={alert.severity === 'critical' ? 'danger' : 'warning'}>
                                                    {alert.severity}
                                                </Badge>
                                            </td>
                                            <td>
                                                {!isWarehouse ? (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => {
                                                            setItems([{
                                                                product: alert.product_id,
                                                                quantity: "",
                                                                unit: "",
                                                                conversion_factor: 1,
                                                            }]);
                                                            setShowRequestModal(true);
                                                        }}
                                                    >
                                                        Request Transfer
                                                    </Button>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted">
                                                No alerts available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}

                        {activeTab === 'requests' && (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Requesting Store</th>
                                        <th>Source Store</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>View</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.length ? requests.map((req) => (
                                        <tr key={req.id}>
                                            <td>#{req.id}</td>
                                            <td>{getStoreName(req.requesting_store)}</td>
                                            <td>{getStoreName(req.source_store)}</td>
                                            <td>
                                                <Badge bg={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'secondary' : 'warning'}>
                                                    {req.status}
                                                </Badge>
                                            </td>
                                            <td>{req.remarks || "-"}</td>
                                            <td>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => openViewModal('request', req)}
                                                    title="View request items"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Button>
                                            </td>
                                            <td>
                                                {isWarehouse && isAdmin && req.status === 'PENDING' ? (
                                                    <>
                                                        <Button
                                                            size="sm" variant="success" className="me-2"
                                                            onClick={() => handleApprove(req.id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="sm" variant="outline-danger"
                                                            onClick={() => handleReject(req.id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="7" className="text-center text-muted">
                                                No transfer requests available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}

                        {activeTab === 'transfers' && (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Remarks</th>
                                        <th>View</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transfers.length ? transfers.map((transfer) => (
                                        <tr key={transfer.id}>
                                            <td>TR-{transfer.id}</td>
                                            <td>{getStoreName(transfer.from_store)}</td>
                                            <td>{getStoreName(transfer.to_store)}</td>
                                            <td>{transfer.remarks || "-"}</td>
                                            <td>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => openViewModal('transfer', transfer)}
                                                    title="View transfer items"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted">
                                                No transfers available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <ViewItemsModal
                show={showViewModal}
                onHide={() => {
                    setShowViewModal(false);
                    setViewModalType(null);
                    setViewRecord(null);
                }}
                type={viewModalType}
                record={viewRecord}
                getProductName={getProductName}
                getUnitLabel={getUnitLabel}
            />

            <TransferModal
                show={showRequestModal}
                onHide={() => setShowRequestModal(false)}
                title="Create Transfer Request"
                handleSubmit={submitRequest}
                items={items}
                productOptions={productOptions}
                stores={stores}
                currentStore={currentStore}
                warehouseStore={warehouseStore}
                isWarehouse={isWarehouse}
                form={requestForm}
                setForm={setRequestForm}
                handleItemChange={handleItemChange}
                handleProductSelect={handleProductSelect}
                handleUnitSelect={handleUnitSelect}
                addLine={addLine}
                removeLine={removeLine}
                mode="request"
            />

            <TransferModal
                show={showTransferModal}
                onHide={() => setShowTransferModal(false)}
                title="Create Direct Transfer"
                handleSubmit={submitTransfer}
                items={items}
                productOptions={productOptions}
                stores={stores}
                currentStore={currentStore}
                warehouseStore={warehouseStore}
                isWarehouse={isWarehouse}
                form={transferForm}
                setForm={setTransferForm}
                handleItemChange={handleItemChange}
                handleProductSelect={handleProductSelect}
                handleUnitSelect={handleUnitSelect}
                addLine={addLine}
                removeLine={removeLine}
                mode="transfer"
            />
        </div>
    );
}

function ViewItemsModal({ show, onHide, type, record, getProductName, getUnitLabel }) {
    const items = Array.isArray(record?.items) ? record.items : [];

    const headerId = type === 'transfer'
        ? (record?.id != null ? `TR-${record.id}` : '-')
        : (record?.id != null ? `#${record.id}` : '-');

    const title = type === 'transfer' ? 'Transfer Details' : 'Transfer Request Details';

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <div><strong>ID:</strong> {headerId}</div>
                    <div><strong>Remarks:</strong> {record?.remarks || '-'}</div>
                </div>

                <Table responsive bordered hover>
                    <thead>
                        <tr>
                            <th style={{ width: '55%' }}>Product</th>
                            <th style={{ width: '20%' }}>Unit</th>
                            <th style={{ width: '25%' }}>Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length ? items.map((it, idx) => (
                            <tr key={`${String(record?.id || 'x')}-${idx}`}
                            >
                                <td>{getProductName(it.product)}</td>
                                <td>{getUnitLabel(it.product, it.unit)}</td>
                                <td>{it.quantity}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={3} className="text-center text-muted">No items available</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

function TransferModal({
                           show,
                           onHide,
                           title,
                           handleSubmit,
                           items,
                           productOptions,
                           stores,
                           currentStore,
                           warehouseStore,
                           isWarehouse,
                           form,
                           setForm,
                           handleItemChange,
                           handleProductSelect,
                           handleUnitSelect,
                           addLine,
                           removeLine,
                           mode,
                       }) {

    const lockRequestStores = mode === 'request' && !isWarehouse;

    const requestingStoreOptions = lockRequestStores
        ? (currentStore ? [currentStore] : [])
        : stores;
    const sourceStoreOptions = lockRequestStores
        ? (warehouseStore ? [warehouseStore] : [])
        : stores;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <Form.Label>{mode === "request" ? "Requesting Store" : "From Store"}</Form.Label>
                                <Form.Select
                                    value={mode === "request" ? form.requesting_store : form.from_store}
                                    onChange={(e) =>
                                        setForm({...form, [mode === "request" ? "requesting_store" : "from_store"]: e.target.value})
                                    }
                                    required
                                    disabled={lockRequestStores && mode === 'request'}
                                >
                                    <option value="">Select Store</option>
                                    {requestingStoreOptions.map((store) => (
                                        <option key={store.id} value={store.id}>
                                            {store.name}
                                        </option>
                                    ))}
                                </Form.Select>
                        </Col>

                        <Col md={6}>
                            <Form.Label>{mode === "request" ? "Source Store" : "To Store"}</Form.Label>
                            <Form.Select
                                value={mode === "request" ? form.source_store : form.to_store}
                                onChange={(e) =>
                                    setForm({...form, [mode === "request" ? "source_store" : "to_store"]: e.target.value})
                                }
                                required
                                disabled={lockRequestStores && mode === 'request'}
                            >
                                <option value="">Select Store</option>
                                {sourceStoreOptions.map((store) => (
                                    <option key={store.id} value={store.id}>
                                        {store.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    {items.map((item, index) => (
                        <Card key={index} className="mb-3 border-0 shadow-sm">
                            <Card.Body>
                                <Row className="g-3 align-items-end">
                                    <Col md={6}>
                                        <Form.Label>Product</Form.Label>
                                        <Select
                                            value={productOptions.find((p) => String(p.value) === String(item.product))}
                                            options={productOptions}
                                            onChange={(selectedOption) =>
                                                handleProductSelect(index, selectedOption ? selectedOption.value : '')}
                                            isSearchable={true}
                                            placeholder="Select Product"
                                        />
                                    </Col>

                                    <Col md={3}>
                                        <Form.Label>Quantity</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={item.quantity}
                                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                            required
                                        />
                                    </Col>

                                    {Array.isArray(item.availableUnits) && item.availableUnits.length > 1 ? (
                                        <Col md={2}>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Select
                                                value={item.unit}
                                                onChange={(e) => handleUnitSelect(index, e.target.value)}
                                                required
                                            >
                                                <option value="">Select unit</option>
                                                {item.availableUnits.map((u) => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.name}{u.abbreviation ? ` (${u.abbreviation})` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                    ) : (
                                        <Col md={2}>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Control
                                                value={
                                                    item.availableUnits?.[0]
                                                        ? `${item.availableUnits[0].name}${item.availableUnits[0].abbreviation ? ` (${item.availableUnits[0].abbreviation})` : ''}`
                                                        : ''
                                                }
                                                placeholder="-"
                                                disabled
                                                readOnly
                                            />
                                        </Col>
                                    )}

                                    <Col md={1}>
                                        {index > 0 && (
                                            <Button
                                                variant="outline-danger"
                                                className="w-100"
                                                onClick={() => removeLine(index)}
                                                title="Remove item"
                                                aria-label="Remove item"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                            )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}

                    <Button variant="outline-secondary" onClick={addLine}>+ Add Item</Button>
                    <Form.Group className="mt-3">
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.remarks}
                            onChange={(e) => setForm({...form, remarks: e.target.value})}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="light" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary">Save</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}